import { Channel } from '@streamelements/client-sdk-js';
import { AllowedProvider } from '../constants/common';
import { HttpListenerService } from './httpListenerService';

type AuthServerEvents = {
  code: (params: { code: string; state: string }) => Promise<void>;
  accessToken: (params: { accessToken: string; tokenType: string; providerId: string }) => Promise<void>;
};

type ProviderEventHandler = {
  twitch: AuthServerEvents['accessToken'];
  tiktok: AuthServerEvents['code'];
};

type AuthEventListenerType<P extends AllowedProvider = AllowedProvider> = P extends keyof ProviderEventHandler
  ? ProviderEventHandler[P]
  : never;

type ConnectListener = (params: { channel: Channel; token: string; tokenType: string }) => Promise<void>;

type TokenListener = (params: { token: string }) => Promise<void>;

export class AuthServer<Provider extends AllowedProvider, EventListener extends AuthEventListenerType<Provider>> {
  private server: HttpListenerService;
  private listeners: Map<'auth' | 'connect' | 'token', Set<EventListener | ConnectListener>> = new Map();
  static port = 4269;
  private provider: Provider;

  constructor({ provider }: { provider: Provider }) {
    this.provider = provider;
    this.server = new HttpListenerService({
      ipAddress: '127.0.0.1',
      // portNumber: AuthServer.port,
      portNumber: null,
    });

    this.server.post(`/auth/${provider}/callback`, async (request) => {
      const listeners = this.listeners.get('auth') ?? new Set();

      listeners.forEach(async (handler) => {
        try {
          const data = await request.json();
          handler(data);
        } catch (error) {
          console.error('Error handling auth callback', error);
        }
      });

      return new Response('ok');
    });

    this.server.post(`/auth/connect/${provider}/callback`, async (request) => {
      const listeners = this.listeners.get('connect') ?? new Set();

      listeners.forEach(async (handler) => {
        try {
          const data = await request.json();
          handler(data);
        } catch (error) {
          console.error('Error handling connect callback', error);
        }
      });

      return new Response('ok');
    });

    this.server.post(`/auth/connect/${provider}/token`, async (request) => {
      const listeners = this.listeners.get('token') ?? new Set();

      listeners.forEach(async (handler) => {
        const data = await request.json();
        handler(data);
      });

      return new Response('ok');
    });

    this.server.get('/healthz', async () => {
      return new Response('ok');
    });
  }

  onAuthCallback(handler: EventListener) {
    const listeners = this.listeners.get('auth') ?? new Set();
    listeners.add(handler);
    this.listeners.set('auth', listeners);

    return () => {
      listeners.delete(handler);
      this.listeners.set('auth', listeners);
    };
  }

  onConnectCallback(handler: ConnectListener) {
    const listeners = this.listeners.get('connect') ?? new Set();
    listeners.add(handler);
    this.listeners.set('connect', listeners);

    return () => {
      listeners.delete(handler);
      this.listeners.set('connect', listeners);
    };
  }

  onToken(handler: TokenListener) {
    const listeners = this.listeners.get('token') ?? new Set();
    listeners.add(handler);
    this.listeners.set('token', listeners);

    return () => {
      listeners.delete(handler);
      this.listeners.set('token', listeners);
    };
  }

  public get url() {
    return `http://${this.server.ipAddress}:${this.server.portNumber}`;
  }

  public get callbackUrl() {
    return `${this.url}/auth/${this.provider}/callback`;
  }

  public get() {
    return this.server;
  }

  async listen() {
    return this.server.listen();
  }

  async dispose() {
    await this.server.stop();
  }
}
